import React, {FormEvent, useEffect, useRef, useState} from "react";
import {
    ConsoleMessage,
    ConsoleMessageLogType,
} from "../interfaces";
import {useLocalStorageT} from "../../../hooks/useLocalStorage";
import {rpc_sendConsoleCommand} from "../rpc";
import {useConsole} from "./ConsoleContext";
import {useSocket} from "../SocketContext";
import ConsoleFilterOverlay from "./ConsoleFilterOverlay";

export default function Console(){
    const console = useConsole()

    const [showFilters, setShowFilters] = useState<boolean>(false)
    const [filters, setFilters] = useLocalStorageT<LocalConsoleFilter[]>("consoleFilters")
    const [showTimestamp, setShowTimestamp] = useLocalStorageT<boolean>("consoleShowTimestamp")
    const [zebraMode, setZebraMode] = useLocalStorageT<boolean>("consoleZebraMode")

    if (showTimestamp === null) {
        setShowTimestamp(true)
    }
    if (zebraMode === null) {
        setZebraMode(false)
    }

    function addFilter(filter: LocalConsoleFilter){
        const newFilters = filters ? [...filters, filter] : [filter]
        setFilters(newFilters)
    }

    function removeFilter(filter: LocalConsoleFilter){
        if (!filters){
            return
        }
        let newFilters: LocalConsoleFilter[] | null = filters.filter(f => f !== filter)
        if (newFilters.length < 1){
            newFilters = null;
        }
        setFilters(newFilters)
    }

    function clearFilters(){
        setFilters(null)
    }

    const plugins = Array.from(
        new Set(
            console.messages.value
                .filter(message => !!message.plugin)
                .map(message => message.plugin!)
        )
    );

    return (<>
        {showFilters && (<ConsoleFilterOverlay hide={() => setShowFilters(false)} />)}
        <div className="size-full flex flex-col sm:p-4">
            <div className="flex flex-col md:flex-row justify-between space-x-2">
                <div className="items-center space-x-4 hidden sm:flex">
                    <b>Filter by:</b>
                    <select id="console-message-type" defaultValue="" onChange={e => {
                        addFilter({messageType: parseInt(e.currentTarget.value)})
                        e.currentTarget.value = "";
                    }}
                            className="text-sm rounded-md block px-2 py-1 bg-neutral-600 text-white focus:ring-primary focus:border-primary">
                        <option value="" disabled>Message Type</option>
                        <option value={ConsoleMessageLogType.Error}>Error</option>
                        <option value={ConsoleMessageLogType.Warning}>Warning</option>
                        <option value={ConsoleMessageLogType.Information}>Information</option>
                    </select>
                    <select id="console-plugin" defaultValue="" onChange={e => {
                        addFilter({messageType: ConsoleMessageLogType.None, plugin: e.currentTarget.value})
                        e.currentTarget.value = "";
                    }}
                            className="text-sm rounded-md block px-2 py-1 bg-neutral-600 text-white focus:ring-primary focus:border-primary">
                        <option value="" disabled>Plugin</option>
                        {plugins.map(p => (<option key={p} value={p}>{p}</option>))}
                    </select>

                    <label htmlFor="console-show-timestamps" className="font-bold">Timestamps:</label>
                    <input id="console-show-timestamps" type="checkbox" checked={showTimestamp!}
                           onChange={() => setShowTimestamp(!showTimestamp)}
                           className="h-5 w-5 rounded-md text-blue-500 focus:ring-blue-500 border-neutral-700 bg-neutral-800"/>

                    <label htmlFor="console-zebra-mode" className="font-bold">Alternating colors:</label>
                    <input id="console-zebra-mode" type="checkbox" checked={zebraMode!}
                           onChange={() => setZebraMode(!zebraMode)}
                           className="h-5 w-5 rounded-md text-blue-500 focus:ring-blue-500 border-neutral-700 bg-neutral-800"/>

                </div>
                <div className="items-center space-x-2 hidden md:flex">
                    <div className="flex items-center">
                        <b className="me-1">{console.messageFilterCount}</b>Message filters active
                    </div>
                    <button className="px-2 py-1 rounded-md bg-primary hover:opacity-90"
                            onClick={() => setShowFilters(true)}>Edit</button>
                </div>
            </div>
            {filters && (<div className="mt-2">
                <div className="space-y-2 space-x-2">
                    {filters.map((filter, i) => {
                        return (<div key={i} className="py-1 px-2 bg-primary rounded-md inline-block">
                            <button className="me-2" onClick={() => removeFilter(filter)}>&#10005;</button>
                            <span className="text-neutral-300">{f_getType(filter)}:</span>
                            {f_getValue(filter)}
                        </div>)
                    })}
                    <button className="underline text-primary" onClick={clearFilters}>Clear all</button>
                </div>
            </div>)}
            <div className="mt-4 flex-grow overflow-auto">
                <InnerConsole/>
            </div>
        </div>
    </>);
}

export function InnerConsole() {
    const historyPointer = useRef<number>(-1)
    const inputRef = useRef<HTMLInputElement>(null)

    const consoleCtx = useConsole();
    const socket = useSocket()

    const [consoleFilters, ] = useLocalStorageT<LocalConsoleFilter[]>("consoleFilters")
    const [showTimestamp, ] = useLocalStorageT<boolean>("consoleShowTimestamp")
    const [zebraMode, ] = useLocalStorageT<boolean>("consoleZebraMode")

    const filterMessageTypes = consoleFilters?.filter(x => x.messageType !== ConsoleMessageLogType.None).map(x => x.messageType) ?? []
    const filterPlugins = consoleFilters?.filter(x => !!x.plugin).map(x => x.plugin) ?? []

    const filter = (msg: ConsoleMessage) => {
        if (filterMessageTypes.length > 0 && !filterMessageTypes.includes(msg.type)) {
            return false
        }
        if (filterPlugins.length > 0 && !filterPlugins.includes(msg.plugin)) {
            return false
        }
        return true
    }

    const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowUp") {
            if (historyPointer.current + 1 >= consoleCtx.commandHistory.count){
                return
            }
            historyPointer.current++
            setInputValue()
        }
        else if (e.key === "ArrowDown") {
            if (historyPointer.current < 0){
                return
            }
            historyPointer.current--
            setInputValue()
        }
        function setInputValue() {
            if (inputRef.current) {
                const historyIdx = historyPointer.current >= 0 ? consoleCtx.commandHistory.count - historyPointer.current - 1 : -1
                inputRef.current.value = consoleCtx.commandHistory.value[historyIdx] ?? ''
            }
        }
    }

    // Handle command submission
    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!inputRef.current) {
            return
        }
        const input = inputRef.current.value;
        if (input.trim() === ""){
            return
        }

        consoleCtx.commandHistory.add(input)
        consoleCtx.messages.add({ type: ConsoleMessageLogType.Input, content: '> ' + input, timestamp: Date.now() / 1000 })


        rpc_sendConsoleCommand(socket, input)

        inputRef.current.value = ""
    };

    const colors = ["text-white", "text-orange-400", "text-red-400", "text-green-400", "text-gray-300"];

    const consoleRef = useRef<HTMLDivElement>(null)
    const [lockScroll, setLockScroll] = useState(false)

    function handleScroll(e: React.UIEvent<HTMLDivElement>) {
        const { offsetHeight, scrollHeight, scrollTop } = e.currentTarget;
        const shouldLock = scrollHeight > scrollTop + offsetHeight + 100;
        if (lockScroll !== shouldLock) {
            setLockScroll(shouldLock)
            console.log("scroll lock", shouldLock);
        }
    }

    // Always scroll down after filters have changed
    useEffect(() => {
        scrollDown()
    }, [consoleFilters]);

    useEffect(() => {
        if (!lockScroll){
            scrollDown();
        }
    }, [consoleCtx.messages]);

    function scrollDown(){
        if (consoleRef.current){
            consoleRef.current.scrollTo(0, consoleRef.current.scrollHeight);
        }
    }

    return (<div
        ref={consoleRef} onScroll={handleScroll} // Attach the ref to the container
        className="bg-black text-white font-mono text-sm p-4 relative sm:rounded-md size-full overflow-y-auto
        [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent
        [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb]:bg-green-500 flex flex-col"
    >
        <div style={{ whiteSpaceCollapse: "preserve", lineBreak: 'anywhere'}}>
            {consoleCtx.messages.value.filter(m => filter(m)).map((entry, index) => (
                <div className={`flex ${zebraMode && 'even:bg-neutral-700'} ${entry.originalCommand && 'my-1'}`} key={index} >
                    {showTimestamp && <span className="flex-shrink-0 me-2 md:me-4">{getTimestamp(entry.timestamp)}</span>}
                    {(entry.originalCommand && <div className="flex-shrink-0 me-2 md:me-4 flex flex-col">
                            <div className="text-xs text-neutral-400 flex" style={{ marginInlineStart: "0.1rem", marginBottom: "-0.2rem"}}>
                                <div className="inline-block" style={{fontSize: "0.75rem", marginTop: "0.2rem"}}>&Gamma;</div>
                                <div className="inline-block">
                                    <span className="text-primary"> {entry.originalRunner}</span> used <span className="text-green-400" >{entry.originalCommand}</span>
                                </div>

                            </div>
                            <span className={colors[entry.type % 1000]}>{entry.content}</span>
                        </div>)
                     || <span className={colors[entry.type % 1000]}>{entry.content}</span>}
                </div>
            ))}
        </div>
        {lockScroll && (<div className="z-20 sticky flex items-center justify-center bottom-16">
            <button onClick={scrollDown}
                    className="px-2 py-1 rounded-md bg-primary hover:opacity-90">Scroll to bottom
            </button>
        </div>)}
        <div className="flex-grow" ></div>
        <form onSubmit={handleSubmit} className="mt-4 flex">
            <span className="text-green-500">&gt;</span>{" "}
            <input
                type="text"
                ref={inputRef}
                onKeyDown={handleKey}
                className="bg-black outline-none w-full ms-2 text-green-500"
                placeholder="Enter command"
                autoFocus
            />
        </form>
        {/* Required for tailwind bundler to include all colors*/}
        <span className={"hidden " + colors.join(" ")}></span>
    </div>)

    function getTimestamp(seconds: number){
        const date = new Date(seconds * 1000);
        return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    }
}

interface LocalConsoleFilter{
    messageType: ConsoleMessageLogType,
    plugin?: string
}

function f_isPlugin(f: LocalConsoleFilter){
    return !!f.plugin;
}

function f_isMessageType(f: LocalConsoleFilter){
    return f.messageType !== ConsoleMessageLogType.None;
}

function f_getValue(f: LocalConsoleFilter){
    if (f_isPlugin(f)){
        return f.plugin!;
    }
    if (f_isMessageType(f)){
        if (f.messageType === ConsoleMessageLogType.Error){
            return "Error"
        }
        if (f.messageType === ConsoleMessageLogType.Warning){
            return "Warning"
        }
        if (f.messageType === ConsoleMessageLogType.Information){
            return "Information"
        }
    }

    return "Unknown"
}

function f_getType(f: LocalConsoleFilter){
    if (f_isPlugin(f)){
        return "plugin"
    }
    if (f_isMessageType(f)){
        return "type"
    }

    return "undefined"
}